// Raise a draft.
//
// Four fields, and three of them are write-once for a reason the form states
// rather than discovers: the payer and the currency are what every line on the
// document will claim, and a database FK checks the currency claim on each one.
// Changing either afterwards would silently reinterpret lines already written,
// so a draft raised against the wrong partner is deleted and raised again —
// which on a draft costs nothing and shows up in the audit log.

import { useMemo, type ReactNode } from "react";
import { InvoiceCreate, InvoiceUpdate, type InvoiceListItem } from "@crm/shared";
import { useInvoiceMutations } from "../../api/mutations.js";
import { usePartners } from "../../api/queries.js";
import { FormDialog } from "../../forms/FormDialog.js";
import { DateField, MonthField, SelectField } from "../../forms/fields.js";
import { useEditor, type Wire } from "../../forms/useEditor.js";

interface InvoiceDraft {
  partner_id: string;
  currency: string;
  service_period: string;
  due_date: string;
}

const LABELS = {
  partner_id: "Payer",
  currency: "Currency",
  service_period: "Covers",
  due_date: "Due date",
} as const;

export interface NewInvoiceDialogProps {
  onClose: () => void;
  onCreated: (invoice: InvoiceListItem) => void;
}

export function NewInvoiceDialog({ onClose, onCreated }: NewInvoiceDialogProps): ReactNode {
  const mutations = useInvoiceMutations();
  // Every partner, unfiltered: the payer list on a new invoice is short and a
  // search box over it would be more chrome than choosing.
  const partners = usePartners({ q: "", topLevelOnly: false, page: 1, pageSize: 200 });

  const toWire = useMemo(
    () =>
      (draft: InvoiceDraft): Wire => ({
        partner_id: draft.partner_id,
        currency: draft.currency,
        service_period: draft.service_period === "" ? null : draft.service_period,
        due_date: draft.due_date,
      }),
    [],
  );

  const editor = useEditor<InvoiceDraft, InvoiceCreate, InvoiceUpdate, InvoiceListItem>({
    mode: { kind: "create" },
    blankDraft: { partner_id: "", currency: "USD", service_period: "", due_date: "" },
    draftFromRecord: (record) => ({
      partner_id: record.partner_id,
      currency: record.currency,
      service_period: record.service_period ?? "",
      due_date: record.due_date,
    }),
    toWire,
    createSchema: InvoiceCreate,
    updateSchema: InvoiceUpdate,
    create: async (body, idempotencyKey) => {
      const created = await mutations.create.mutateAsync({ body, idempotencyKey });
      onCreated(created);
      return created;
    },
    update: (id, body, version) => mutations.update.mutateAsync({ id, body, version }),
    fetchCurrent: mutations.fetchCurrent,
    onSaved: () => {
      // `onCreated` already navigated; closing again would race the route change.
    },
  });

  const partnerOptions = [
    { value: "", label: "— choose —" },
    ...(partners.data?.items ?? []).map((partner) => ({
      value: partner.id,
      label: partner.name,
    })),
  ];

  return (
    <FormDialog
      open
      onClose={onClose}
      title="New invoice"
      description="A draft you lay out and then issue. The payer and the currency are fixed once it exists — every line on it will claim both."
      submitLabel="Create draft"
      editor={editor}
      labels={LABELS}
    >
      <SelectField
        label={LABELS.partner_id}
        required
        hint="Who the document is addressed to. It may carry lines from several of their deals."
        value={editor.draft.partner_id}
        options={partnerOptions}
        error={editor.errors["partner_id"]}
        onChange={(value) => editor.setField("partner_id", value)}
      />

      <SelectField
        label={LABELS.currency}
        required
        hint="Every line must be in this currency. A payer billed in two is two documents."
        value={editor.draft.currency}
        options={[
          { value: "CAD", label: "CAD" },
          { value: "USD", label: "USD" },
        ]}
        error={editor.errors["currency"]}
        onChange={(value) => editor.setField("currency", value)}
      />

      <MonthField
        label={LABELS.service_period}
        hint="The month this bill is for, when it is for one. Leave blank on a mixed run."
        value={editor.draft.service_period}
        error={editor.errors["service_period"]}
        onChange={(value) => editor.setField("service_period", value)}
      />

      <DateField
        label={LABELS.due_date}
        required
        value={editor.draft.due_date}
        error={editor.errors["due_date"]}
        onChange={(value) => editor.setField("due_date", value)}
      />
    </FormDialog>
  );
}
